import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { SendHorizonal, Radio, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useLang } from "@/App";
import { Logo } from "@/components/Navbar";
import ParallaxLayer from "@/components/ParallaxLayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatSection() {
    const { lang, t, persona, setPersona, speak, setSpeak, voicePref, userAvatar } = useLang();
    const [sessionId] = useState(() => {
        let s = localStorage.getItem("neon_session");
        if (!s) {
            s = crypto.randomUUID();
            localStorage.setItem("neon_session", s);
        }
        return s;
    });
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const [listening, setListening] = useState(false);
    const [speaking, setSpeaking] = useState(false);
    const [online, setOnline] = useState(() => navigator.onLine);
    const [duoMode, setDuoMode] = useState(false);
    const [duoPick, setDuoPick] = useState([]);
    const [latency, setLatency] = useState(null);
    const scrollRef = useRef(null);
    const recRef = useRef(null);
    const audioRef = useRef(null);
    const speakRef = useRef(speak);
    speakRef.current = speak;

    useEffect(() => {
        if (!speak) {
            audioRef.current?.pause();
            setSpeaking(false);
        }
    }, [speak]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(`${API}/chat/history/${sessionId}`);
                const data = await res.json();
                if (!cancelled && data.messages?.length) {
                    setMessages(data.messages.map((m) => ({ role: m.role, content: m.content })));
                    return;
                }
            } catch {
                /* fall through to greeting */
            }
            if (!cancelled) setMessages([{ role: "assistant", content: t.chat.greetings.neon, greeting: true }]);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    useEffect(() => {
        setMessages((m) =>
            m.length === 1 && m[0].greeting
                ? [{ role: "assistant", content: t.chat.greetings[persona], greeting: true }]
                : m
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang, persona]);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        const on = () => setOnline(true);
        const off = () => setOnline(false);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => {
            window.removeEventListener("online", on);
            window.removeEventListener("offline", off);
        };
    }, []);

    const offlineReply = (text) => {
        setMessages((m) => [
            ...m,
            { role: "user", content: text },
            { role: "assistant", content: t.chat.offline[persona] || t.chat.offline.neon, offline: true },
        ]);
        toast(t.chat.offlineToast, { icon: "📡" });
    };

    const activePersona = t.chat.personas.find((p) => p.id === persona) || t.chat.personas[0];

    const playTts = async (text, personaId) => {
        try {
            setSpeaking(true);
            const res = await fetch(`${API}/tts`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text, persona: personaId, voice_pref: voicePref }),
            });
            if (!res.ok) return;
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            audioRef.current?.pause();
            const a = new Audio(url);
            a.onended = () => setSpeaking(false);
            a.onerror = () => setSpeaking(false);
            audioRef.current = a;
            a.play();
        } catch {
            setSpeaking(false);
        }
    };

    const execCommand = (raw) => {
        const s = raw.toLowerCase();
        const callMatch = s.match(/(зателефонуй|подзвони|набери|call)\s+(.+)/);
        if (callMatch) {
            const name = callMatch[2].trim();
            window.location.href = `tel:${encodeURIComponent(name)}`;
            toast.success(`☎️ ${name}`);
            return true;
        }
        const msgMatch = s.match(/(напиши|sms|повідомлення|message)\s+(.+)/);
        if (msgMatch) {
            window.location.href = `sms:${encodeURIComponent(msgMatch[2].trim())}`;
            toast.success(`💬 SMS`);
            return true;
        }
        const appMatch = s.match(/(відкрий|запусти|open|launch)\s+(.+)/);
        if (appMatch) {
            const app = appMatch[2].trim();
            const maps = {
                "карти": "https://maps.google.com", "maps": "https://maps.google.com",
                "youtube": "https://youtube.com", "ютуб": "https://youtube.com",
                "instagram": "https://instagram.com", "інстаграм": "https://instagram.com",
                "spotify": "https://open.spotify.com", "спотіфай": "https://open.spotify.com",
                "telegram": "https://web.telegram.org", "телеграм": "https://web.telegram.org",
                "камера": "https://webcamtests.com", "camera": "https://webcamtests.com",
                "калькулятор": "https://www.google.com/search?q=calculator", "calculator": "https://www.google.com/search?q=calculator",
                "браузер": "https://google.com", "browser": "https://google.com",
                "годинник": "https://time.is", "clock": "https://time.is",
            };
            const url = maps[app] || maps[app.split(" ")[0]];
            if (url) {
                window.open(url, "_blank");
                toast.success(`▶ ${app}`);
                return true;
            }
        }
        return false;
    };

    const toggleVoice = () => {
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SR) {
            toast.error(t.chat.voiceUnsupported);
            return;
        }
        if (listening) {
            recRef.current?.stop();
            return;
        }
        const rec = new SR();
        rec.lang = lang === "ua" ? "uk-UA" : "en-US";
        rec.interimResults = true;
        rec.continuous = false;
        rec.onresult = (e) => {
            let txt = "";
            for (const r of e.results) txt += r[0].transcript;
            setInput(txt);
            // live command execution in background voice mode
            if (txt && execCommand(txt)) {
                setInput("");
                rec.stop();
                setListening(false);
            }
        };
        rec.onend = () => setListening(false);
        rec.onerror = () => setListening(false);
        recRef.current = rec;
        rec.start();
        setListening(true);
    };

    const sendDuo = async (text) => {
        const [pa, pb] = duoPick;
        setStreaming(true);
        setLatency(null);
        setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", persona: pa }]);
        let currentPersona = pa;
        let fullText = "";
        try {
            const res = await fetch(`${API}/chat/duo`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId, message: text, lang, persona_a: pa, persona_b: pb, turns: 4 }),
            });
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const parts = buf.split("\n\n");
                buf = parts.pop();
                for (const part of parts) {
                    if (!part.startsWith("data: ")) continue;
                    let payload;
                    try {
                        payload = JSON.parse(part.slice(6));
                    } catch {
                        continue;
                    }
                    if (payload.speaker) {
                        currentPersona = payload.speaker;
                        if (fullText.trim() && speakRef.current) playTts(fullText, currentPersona === pa ? pb : pa);
                        fullText = "";
                    } else if (payload.delta) {
                        fullText += payload.delta;
                        setMessages((m) => {
                            const copy = [...m];
                            const last = copy[copy.length - 1];
                            if (last.role === "assistant" && last.persona === currentPersona) {
                                copy[copy.length - 1] = { ...last, content: last.content + payload.delta };
                            } else {
                                copy.push({ role: "assistant", content: payload.delta, persona: currentPersona });
                            }
                            return copy;
                        });
                    } else if (payload.done && fullText.trim() && speakRef.current) {
                        playTts(fullText, currentPersona);
                    } else if (payload.error) {
                        setMessages((m) => {
                            const copy = [...m];
                            copy[copy.length - 1] = { role: "assistant", content: t.chat.error };
                            return copy;
                        });
                    }
                }
            }
        } catch {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                    role: "assistant",
                    content: t.chat.offline.duo,
                    offline: true,
                };
                return copy;
            });
            toast(t.chat.offlineToast, { icon: "📡" });
        }
        setStreaming(false);
    };

    const send = async (raw) => {
        const text = (raw ?? input).trim();
        if (!text || streaming) return;
        if (duoMode && duoPick.length === 2) {
            recRef.current?.stop();
            audioRef.current?.pause();
            setSpeaking(false);
            setInput("");
            if (!online) {
                offlineReply(text);
                return;
            }
            await sendDuo(text);
            return;
        }
        recRef.current?.stop();
        audioRef.current?.pause();
        setSpeaking(false);
        setInput("");
        if (!online) {
            offlineReply(text);
            return;
        }
        setStreaming(true);
        setLatency(null);
        setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "", persona }]);
        const t0 = performance.now();
        const personaAtSend = persona;
        let fullText = "";
        try {
            const res = await fetch(`${API}/chat/stream`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ session_id: sessionId, message: text, lang, persona: personaAtSend }),
            });
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = "";
            let firstToken = false;
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const parts = buf.split("\n\n");
                buf = parts.pop();
                for (const part of parts) {
                    if (!part.startsWith("data: ")) continue;
                    let payload;
                    try {
                        payload = JSON.parse(part.slice(6));
                    } catch {
                        continue;
                    }
                    if (payload.delta) {
                        fullText += payload.delta;
                        if (!firstToken) {
                            firstToken = true;
                            setLatency(Math.round(performance.now() - t0));
                        }
                        setMessages((m) => {
                            const copy = [...m];
                            copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + payload.delta };
                            return copy;
                        });
                    } else if (payload.error) {
                        setMessages((m) => {
                            const copy = [...m];
                            copy[copy.length - 1] = { ...copy[copy.length - 1], content: t.chat.error };
                            return copy;
                        });
                    } else if (payload.done && speakRef.current && fullText.trim()) {
                        playTts(fullText, personaAtSend);
                    }
                }
            }
        } catch {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                    role: "assistant",
                    content: t.chat.offline[persona] || t.chat.offline.neon,
                    offline: true,
                    persona,
                };
                return copy;
            });
            toast(t.chat.offlineToast, { icon: "📡" });
        }
        setStreaming(false);
    };

    return (
        <section id="chat" className="relative pt-20 pb-8 md:pb-28 lg:py-28 overflow-hidden" data-testid="chat-section">
            <ParallaxLayer
                speed={-60}
                testid="chat-parallax-1"
                className="absolute right-[-70px] top-8 w-[400px] h-[560px] char-frame bg-cover bg-center opacity-[0.09] pointer-events-none"
                style={{ backgroundImage: "url(https://images.unsplash.com/photo-1780702335071-06e6e65ba5f8?q=80&w=800&auto=format&fit=crop)" }}
            />
            <ParallaxLayer
                speed={70}
                testid="chat-parallax-2"
                className="absolute left-[-90px] bottom-0 w-[360px] h-[500px] char-frame bg-cover bg-center opacity-[0.07] pointer-events-none"
                style={{ backgroundImage: "url(https://images.unsplash.com/flagged/photo-1579451443170-44b3963c3341?q=80&w=800&auto=format&fit=crop)" }}
            />
            <div className="kana kana-watermark absolute left-2 top-8 text-[7rem] lg:text-[10rem] leading-none pointer-events-none" aria-hidden="true">
                チャット
            </div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="mb-10"
                >
                    <div className="font-mono2 text-[11px] tracking-[0.3em] text-[#ff4bd8] pk-text">{t.chat.kicker}</div>
                    <h2 className="font-display font-bold uppercase tracking-wide text-2xl sm:text-3xl lg:text-4xl mt-3">
                        {t.chat.title}
                    </h2>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.7 }}
                    className="max-w-4xl mx-auto mb-7"
                >
                    <div className="font-mono2 text-[10px] tracking-[0.3em] text-slate-500 mb-4 flex items-center gap-3">
                        {t.chat.personaTitle}
                        <span className="warning-stripes w-12 h-1.5 opacity-70" />
                        <button
                            data-testid="duo-toggle"
                            onClick={() => {
                                setDuoMode((d) => {
                                    if (d) setDuoPick([]);
                                    return !d;
                                });
                            }}
                            className={`ml-auto font-mono2 text-[9px] tracking-[0.2em] rounded-full border px-3.5 py-1.5 transition-colors ${
                                duoMode
                                    ? "border-[#ff4bd8] text-[#ff4bd8] bg-[#ff4bd8]/10 shadow-[0_0_14px_rgba(255,75,216,0.3)]"
                                    : "border-[#2c3a5c] text-slate-400 hover:border-[#ff4bd8]/60 hover:text-[#ff4bd8]"
                            }`}
                        >
                            {t.chat.duo.label}
                        </button>
                    </div>
                    {duoMode && (
                        <div className="font-mono2 text-[10px] text-[#ff4bd8]/80 tracking-wider mb-3" data-testid="duo-hint">
                            {duoPick.length < 2 ? t.chat.duo.needTwo : `${t.chat.duo.tag}: ${duoPick.map((id) => t.chat.personas.find((p) => p.id === id)?.name).join(" × ")}`}
                        </div>
                    )}
                    <div className="flex gap-4 sm:gap-5 flex-wrap" data-testid="persona-selector">
                        {t.chat.personas.map((p) => {
                            const selected = duoMode ? duoPick.includes(p.id) : persona === p.id;
                            return (
                                <button
                                    key={p.id}
                                    data-testid={`persona-${p.id}`}
                                    onClick={() => {
                                        if (duoMode) {
                                            setDuoPick((prev) =>
                                                prev.includes(p.id)
                                                    ? prev.filter((x) => x !== p.id)
                                                    : prev.length < 2
                                                      ? [...prev, p.id]
                                                      : [prev[1], p.id]
                                            );
                                        } else {
                                            setPersona(p.id);
                                        }
                                    }}
                                    className="flex flex-col items-center gap-1.5 group"
                                >
                                    <span
                                        className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden border-2 transition-all duration-300 avatar-breathe ${
                                            selected
                                                ? "border-[#fcee0a] shadow-[0_0_20px_rgba(252,238,10,0.45)] scale-105"
                                                : "border-[#2c3a5c] grayscale-[55%] group-hover:grayscale-0 group-hover:border-[#49f5e8]/60"
                                        }`}
                                    >
                                        {p.avatar ? (
                                            <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="w-full h-full grid place-items-center bg-[#0e1322]">
                                                <Logo size={30} />
                                            </span>
                                        )}
                                    </span>
                                    <span className={`font-mono2 text-[9px] tracking-[0.2em] ${selected ? "text-[#fcee0a]" : "text-slate-500"}`}>
                                        {p.name}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="hud-corner glow-card rounded-2xl overflow-hidden max-w-4xl mx-auto"
                >
                    <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-[#212b45] bg-[#0a0e1a]/90">
                        <div className="flex items-center gap-3">
                            <Radio size={15} className="text-[#d7ff35]" />
                            <span className="font-display text-xs tracking-[0.2em] font-bold uppercase" data-testid="chat-active-persona">
                                {duoMode && duoPick.length === 2
                                    ? t.chat.personas
                                          .filter((p) => duoPick.includes(p.id))
                                          .map((p) => p.name)
                                          .join(" × ")
                                    : activePersona.name}{" "}
                                <span className="text-slate-500 font-mono2 text-[9px]">
                                    // {duoMode && duoPick.length === 2 ? t.chat.duo.tag : activePersona.tag}
                                </span>
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            {latency !== null && (
                                <span className="font-mono2 text-[10px] text-slate-500" data-testid="chat-latency">
                                    TTFB {latency}MS
                                </span>
                            )}
                            <button
                                data-testid="chat-tts-toggle"
                                onClick={() => setSpeak((s) => !s)}
                                title={t.chat.speak}
                                className={`flex items-center gap-2 font-mono2 text-[9px] tracking-[0.15em] rounded border px-2 py-1 transition-colors ${
                                    speak
                                        ? "text-[#ff4bd8] border-[#ff4bd8]/50 bg-[#ff4bd8]/10"
                                        : "text-slate-500 border-[#2c3a5c] hover:text-[#ff4bd8]"
                                }`}
                            >
                                {speak ? <Volume2 size={12} className={speaking ? "pulse-dot" : ""} /> : <VolumeX size={12} />}
                                <span className="hidden sm:inline">{t.chat.speak}</span>
                            </button>
                            <span className="font-mono2 text-[9px] tracking-[0.2em] text-[#d7ff35] border border-[#d7ff35]/40 rounded px-2 py-1" data-testid="chat-live-tag">
                                {t.chat.tag}
                            </span>
                            {!online && (
                                <span className="font-mono2 text-[9px] tracking-[0.2em] text-[#ff4bd8] border border-[#ff4bd8]/50 rounded px-2 py-1" data-testid="chat-offline-tag">
                                    {t.chat.offlineTag}
                                </span>
                            )}
                        </div>
                    </div>

                    <div ref={scrollRef} className="chat-scroll h-[380px] sm:h-[440px] overflow-y-auto px-4 sm:px-6 py-5 flex flex-col gap-3.5" data-testid="chat-messages">
                        {messages.map((m, i) => (
                            <div
                                key={i}
                                className={`flex items-end gap-2.5 ${m.role === "user" ? "self-end flex-row-reverse" : "self-start"}`}
                            >
                                {m.role === "assistant" && (
                                    <span
                                        className={`w-7 h-7 rounded-full overflow-hidden border border-[#fcee0a]/50 shrink-0 grid place-items-center bg-[#0e1322] avatar-breathe ${
                                            speaking && i === messages.length - 1 ? "speaking" : ""
                                        }`}
                                        data-testid={`chat-speaker-avatar-${i}`}
                                    >
                                        {(() => {
                                            const p = t.chat.personas.find((p) => p.id === (m.persona || persona));
                                            return p?.avatar ? (
                                                <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <Logo size={16} />
                                            );
                                        })()}
                                    </span>
                                )}
                                {m.role === "user" && (
                                    <span className="w-7 h-7 rounded-full overflow-hidden border border-[#b56dff]/60 shrink-0 grid place-items-center bg-[#1c0f33]" data-testid="chat-user-avatar">
                                        {userAvatar ? (
                                            <img src={userAvatar} alt="you" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="font-display font-bold text-[10px] text-[#b56dff]">YOU</span>
                                        )}
                                    </span>
                                )}
                                <div
                                data-testid={`chat-msg-${m.role}-${i}`}
                                className={`max-w-[86%] sm:max-w-[75%] px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap rounded-2xl ${
                                    m.role === "user"
                                        ? "bg-gradient-to-br from-[#3a1d63] to-[#5b21a0] border border-[#b56dff]/40 rounded-tr-sm"
                                        : "bg-[#131a2c] border border-[#26324e] rounded-tl-sm text-slate-200"
                                } ${streaming && i === messages.length - 1 && m.role === "assistant" ? "cursor-blink" : ""}`}
                            >
                                {m.offline && (
                                    <span className="block font-mono2 text-[8px] tracking-[0.25em] text-[#fcee0a] mb-1.5">
                                        ▚ {t.chat.offlineTag} CACHE
                                    </span>
                                )}
                                {m.content}
                                </div>
                            </div>
                        ))}
                        {streaming && messages[messages.length - 1]?.content === "" && (
                            <div className="self-start flex items-end gap-1 h-6 px-2" data-testid="chat-typing-indicator">
                                {[0, 1, 2, 3, 4].map((i) => (
                                    <span key={i} className="eq-bar w-1 h-5 bg-[#49f5e8]/80 rounded-sm" style={{ animationDelay: `${i * 0.12}s` }} />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex gap-2 flex-wrap px-4 sm:px-6 pb-4" data-testid="chat-quick-prompts">
                        {t.chat.quick.map((q, i) => (
                            <button
                                key={q.label}
                                data-testid={`quick-prompt-${i}`}
                                onClick={() => send(q.prompt)}
                                disabled={streaming}
                                className="font-mono2 text-[11px] px-3.5 py-2 rounded-full border border-[#2c3a5c] bg-[#101728] text-slate-300 hover:border-[#d7ff35]/60 hover:text-[#d7ff35] transition-colors disabled:opacity-40"
                            >
                                {q.label}
                            </button>
                        ))}
                    </div>

                    {listening && (
                        <div className="flex items-center gap-2.5 px-6 pb-3" data-testid="voice-listening-indicator">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#ff4bd8] pulse-dot" />
                            <div className="flex items-end gap-1 h-4">
                                {[0, 1, 2, 3, 4, 5].map((i) => (
                                    <span key={i} className="eq-bar w-[3px] h-3.5 bg-[#ff4bd8]/80 rounded-sm" style={{ animationDelay: `${i * 0.09}s` }} />
                                ))}
                            </div>
                            <span className="font-mono2 text-[10px] tracking-[0.2em] text-[#ff4bd8]">{t.chat.listening}</span>
                        </div>
                    )}

                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            send();
                        }}
                        className="flex gap-3 px-4 sm:px-6 py-4 border-t border-[#212b45] bg-[#0a0e1a]/90 md:static fixed bottom-[76px] left-0 right-0 z-40 md:z-auto"
                        data-testid="chat-composer"
                    >
                        <div className="flex flex-col items-center gap-1.5">
                            <button
                                data-testid="chat-voice-btn"
                                type="button"
                                onClick={toggleVoice}
                                className={`w-[50px] rounded-xl border grid place-items-center transition-colors ${
                                    listening
                                        ? "border-[#ff4bd8] text-[#ff4bd8] bg-[#ff4bd8]/10 shadow-[0_0_18px_rgba(255,75,216,0.35)]"
                                        : "border-[#2c3a5c] text-slate-400 hover:border-[#ff4bd8]/60 hover:text-[#ff4bd8]"
                                }`}
                                aria-label="Voice input"
                            >
                                {listening ? <MicOff size={17} /> : <Mic size={17} />}
                            </button>
                            <span className="font-mono2 text-[7px] tracking-[0.15em] text-slate-600 hidden sm:block" data-testid="voice-cmd-hint">
                                {t.chat.voiceCmd.label}
                            </span>
                        </div>
                        <input
                            data-testid="chat-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder={listening ? t.chat.listening : t.chat.placeholder}
                            autoComplete="off"
                            className="flex-1 min-w-0 bg-[#080d17] border border-[#2c3a5c] rounded-xl px-4 py-3.5 text-sm outline-none focus:border-[#49f5e8] focus:shadow-[0_0_0_3px_rgba(73,245,232,0.12)] transition-shadow placeholder:text-slate-600"
                        />
                        <button
                            data-testid="chat-send-btn"
                            type="submit"
                            disabled={streaming || !input.trim()}
                            className="flex items-center gap-2 bg-[#d7ff35] text-[#080a12] font-bold text-sm px-5 sm:px-6 rounded-xl hover:shadow-[0_0_26px_rgba(215,255,53,0.5)] transition-shadow disabled:opacity-40"
                        >
                            <SendHorizonal size={16} />
                            <span className="hidden sm:inline">{t.chat.send}</span>
                        </button>
                    </form>
                </motion.div>

            </div>
        </section>
    );
}
